/**
 * Tab "Nghiệp vụ đã khai báo" — phần thuần của `DaKhaiTab.tsx` native.
 *
 * `ReplicaColumn` của backend khai alias camelCase (`nullMeaning`, `enumValues`) ở CẢ hai chiều. Bản native từng viết
 * snake_case ở màn này và hỏng hai kiểu: đọc ra `undefined`, ghi gửi CẢ HAI khoá và ăn 422 `extra="forbid"`.
 */

export interface CotKhai {
  name: string;
  type?: string;
  unit?: string | null;
  label?: string | null;
  additive?: boolean | null;
  nullMeaning?: string | null;
  enumValues?: { value: string; meaning?: string | null }[];
}

export interface BangKhai {
  name: string;
  grain?: string;
  enabled?: boolean;
  /** Đã có người trả lời "trợ lý có được dùng bảng này không". */
  phamViDaXacNhan?: boolean;
  columns?: CotKhai[];
  [k: string]: unknown;
}

/**
 * Ba trạng thái của `additive` (và của câu phạm vi), dạng chuỗi để ở chung `sua` với ô chữ. `''` = "chưa khai" và nó
 * KHÁC "không": chưa khai thì cổng chặn `SUM` và câu hỏi quay lại màn Phỏng vấn; đã khai `false` thì nó im.
 */
export const BA_TRANG_THAI = ['co', 'khong', ''] as const;
export type BaTrangThai = (typeof BA_TRANG_THAI)[number];

export function phamViCua(b: BangKhai): BaTrangThai {
  if (!b.phamViDaXacNhan) return '';
  return b.enabled === false ? 'khong' : 'co';
}

export function docCongDon(v: string): boolean | null {
  return v === 'co' ? true : v === 'khong' ? false : null;
}

export function congDonCua(v: boolean | null | undefined): BaTrangThai {
  return v === true ? 'co' : v === false ? 'khong' : '';
}

/** Cột đã có người khai gì chưa. Cột trắng trơn không đáng chiếm chỗ — chỗ của nó là màn Phỏng vấn. */
export function daKhaiGi(c: CotKhai): boolean {
  return Boolean(
    (c.label || '').trim() ||
      (c.unit || '').trim() ||
      (c.additive ?? null) !== null ||
      (c.nullMeaning || '').trim() ||
      (c.enumValues?.length ?? 0) > 0,
  );
}

/**
 * Bảng như lúc vừa chọn từ CSDL: giữ cấu trúc (cột, khoá chính, cách đồng bộ), để trắng mọi câu trả lời nghiệp vụ.
 * "Xoá khai báo" ở đây là xoá LỜI KHAI, không phải gỡ bảng — gỡ bảng là mất lịch đồng bộ, mất phân quyền.
 */
export function boLoiKhai(b: BangKhai): BangKhai {
  return {
    ...b,
    grain: '',
    enabled: true,
    phamViDaXacNhan: false,
    kieuBang: null,
    columns: (b.columns ?? []).map((c) => ({
      ...c,
      label: null,
      unit: null,
      additive: null,
      nullMeaning: null,
      enumValues: [],
    })),
  };
}

/**
 * Những bảng hiện ở tab: có cột đã khai, có `grain`, đã trả lời phạm vi, hoặc đang tắt. Bảng trả lời "Không — bỏ bảng
 * này ra" chưa khai gì khác vẫn phải hiện — không thì chọn nhầm "Không" cho cả nguồn là mất trắng, không còn chỗ bật lại.
 * Bảng tắt lên đầu, rồi theo số cột đã khai.
 */
export function bangHien(raw: BangKhai[], chiBangTat: boolean): (BangKhai & { cot: CotKhai[] })[] {
  return raw
    .map((b) => ({ ...b, cot: (b.columns ?? []).filter(daKhaiGi) }))
    .filter((b) => b.cot.length > 0 || (b.grain || '').trim() || b.phamViDaXacNhan || b.enabled === false)
    .filter((b) => !chiBangTat || b.enabled === false)
    .sort((a, b) => Number(b.enabled === false) - Number(a.enabled === false) || b.cot.length - a.cot.length);
}

/**
 * Bảng cần gửi khi Lưu, đã áp các ô đang treo. Khoá ô là `<bảng>.<cột>.<trường>` (phạm vi và grain: `<bảng>..<trường>`).
 * Tên bảng có thể chứa dấu chấm, nên so bằng TIỀN TỐ. Chỉ gửi bảng có ô treo (upsert, không xoá bảng vắng mặt), nhưng
 * gửi NGUYÊN object bảng: `spec` bị ghi đè trọn, thiếu trường nào là mất trường ấy.
 */
export function bangCanGui(raw: BangKhai[], sua: Record<string, string>): BangKhai[] {
  const khoaSua = Object.keys(sua);
  return raw
    .filter((b) => khoaSua.some((k) => k.startsWith(`${b.name}.`)))
    .map((b) => {
      const kPv = `${b.name}..phamVi`;
      const kGrain = `${b.name}..grain`;
      return {
        ...b,
        ...(kPv in sua ? { enabled: sua[kPv] !== 'khong', phamViDaXacNhan: sua[kPv] !== '' } : {}),
        grain: kGrain in sua ? sua[kGrain] : b.grain,
        columns: (b.columns ?? []).map((c) => {
          const k = (f: string) => `${b.name}.${c.name}.${f}`;
          return {
            ...c,
            label: k('label') in sua ? sua[k('label')] : c.label,
            unit: k('unit') in sua ? sua[k('unit')] : c.unit,
            nullMeaning: k('nullMeaning') in sua ? sua[k('nullMeaning')] : c.nullMeaning,
            additive: k('additive') in sua ? docCongDon(sua[k('additive')] ?? '') : c.additive,
          };
        }),
      };
    });
}
